import {
  normalizeValue,
  resolveMediaRecordStatusValue,
  resolveScanStatusValue
} from './statusNormalization';
import {
  isFailedStatus,
  isSuccessfulLifecycleStatus,
  isSuccessfulStatus,
  isVirusStatus
} from './statusPredicates';
import {
  coerceRecordValue,
  getFirstRawRecordValue,
  getFirstRecordValue,
  getRecordDisplayStatus as getRecordDisplayStatusValue,
  getRecordFileType,
  getRecordLogicalName,
  getRecordMediaId,
  getRecordMimeType,
  getRecordOriginalFileName,
  getRecordSizeInBytes,
  getRecordTranscriptFileId,
  inferRecordExtension,
  isPlainObject,
  normalizeDisplayName
} from './caseFileRecordAccessors';

export function createCaseFileRecordStatusHelpers({
  getEffectiveTranscriptRecord,
  resolveScanStatusTitle,
  isUploadWorkflowComplete,
  MEDIA_EXTENSIONS,
  TRANSCRIPT_EXTENSIONS
}) {
  function getAllRecordValues(record, fieldNames) {
    if (!record) {
      return [];
    }

    const values = [];
    const targetFields = new Set((fieldNames ?? []).map((fieldName) => String(fieldName).toLowerCase()));
    Object.entries(record).forEach(([key, value]) => {
      const normalizedKey = String(key).toLowerCase();
      if (targetFields.has(normalizedKey)
        || normalizedKey.includes('virus')
        || normalizedKey.includes('malware')
        || normalizedKey.includes('threat')
        || normalizedKey.includes('scan')
        || normalizedKey.includes('verdict')
        || normalizedKey.includes('result')
        || normalizedKey.includes('status')) {
        const coercedValue = coerceRecordValue(value);
        if (String(coercedValue).trim() !== '') {
          values.push(coercedValue);
        }
      }
    });

    return values;
  }

  function isTruthyRecordFlag(value) {
    if (value === true) {
      return true;
    }

    const normalized = normalizeValue(value);
    return normalized === 'true'
      || normalized === '1'
      || normalized === 'yes'
      || normalized === 'y'
      || normalized === 'detected'
      || normalized === 'found';
  }

  function getRecordScanStatus(record) {
    return getFirstRecordValue(record, [
      'scanStatus',
      'scanResult',
      'scanResultStatus',
      'virusScanStatus',
      'virusScanResult',
      'antivirusScanStatus',
      'antiVirusScanStatus',
      'malwareScanStatus',
      'malwareScanResult',
      'threatScanStatus',
      'avScanStatus',
      'fileScanStatus',
      'mediaScanStatus',
      'transcriptScanStatus',
      'threatScanResult',
      'status',
      'scanVerdict',
      'verdict'
    ]);
  }

  function getRecordLifecycleStatus(record) {
    return getFirstRecordValue(record, [
      'recordStatus',
      'status',
      'fileStatus',
      'uploadStatus',
      'workflowStatus',
      'processingStatus'
    ]);
  }

  function getRecordDisplayStatus(record) {
    return getRecordDisplayStatusValue(record, getRecordScanStatus, getRecordLifecycleStatus);
  }

  function isVirusDetectedRecord(record) {
    if (!record) {
      return false;
    }

    const explicitVirusFlag = getFirstRecordValue(record, [
      'hasVirus',
      'HasVirus',
      'virusDetected',
      'VirusDetected',
      'isVirusDetected',
      'IsVirusDetected',
      'malwareDetected',
      'MalwareDetected',
      'isMalwareDetected',
      'IsMalwareDetected',
      'threatDetected',
      'ThreatDetected',
      'isThreatDetected',
      'IsThreatDetected',
      'threatFound',
      'ThreatFound',
      'isThreatFound',
      'IsThreatFound',
      'infected',
      'Infected',
      'isInfected',
      'IsInfected',
      'quarantined',
      'Quarantined',
      'isQuarantined',
      'IsQuarantined'
    ]);

    if (isTruthyRecordFlag(explicitVirusFlag)) {
      return true;
    }

    const scanValues = getAllRecordValues(record, [
      'scanStatus',
      'scanResult',
      'scanResultStatus',
      'virusScanStatus',
      'virusScanResult',
      'antivirusScanStatus',
      'antiVirusScanStatus',
      'malwareScanStatus',
      'malwareScanResult',
      'threatScanStatus',
      'threatScanResult',
      'avScanStatus',
      'fileScanStatus',
      'mediaScanStatus',
      'transcriptScanStatus',
      'transcriptScanResult',
      'transcriptVirusScanStatus',
      'transcriptMalwareScanStatus',
      'transcriptThreatScanStatus',
      'recordStatus',
      'status',
      'fileStatus',
      'uploadStatus',
      'workflowStatus',
      'processingStatus',
      'verdict',
      'scanVerdict'
    ]);

    const nestedTranscript = getFirstRawRecordValue(record, [
      'transcript',
      'Transcript',
      'transcriptFile',
      'TranscriptFile',
      'transcriptRecord',
      'TranscriptRecord',
      'transcriptMetadata',
      'TranscriptMetadata',
      'transcriptAttachment',
      'TranscriptAttachment'
    ]);

    if (isPlainObject(nestedTranscript)) {
      scanValues.push(...getAllRecordValues(nestedTranscript, [
        'scanStatus',
        'scanResult',
        'virusScanStatus',
        'malwareScanStatus',
        'threatScanStatus',
        'recordStatus',
        'status',
        'fileStatus',
        'workflowStatus',
        'processingStatus',
        'verdict',
        'scanVerdict'
      ]));
    }

    return scanValues.some((value) => isVirusStatus(value));
  }

  function isVirusDetectedMediaRecord(record) {
    if (!record) {
      return false;
    }

    const explicitVirusFlag = getFirstRecordValue(record, [
      'hasVirus',
      'HasVirus',
      'virusDetected',
      'VirusDetected',
      'isVirusDetected',
      'IsVirusDetected',
      'malwareDetected',
      'MalwareDetected',
      'isMalwareDetected',
      'IsMalwareDetected',
      'threatDetected',
      'ThreatDetected',
      'isThreatDetected',
      'IsThreatDetected',
      'threatFound',
      'ThreatFound',
      'isThreatFound',
      'IsThreatFound',
      'infected',
      'Infected',
      'isInfected',
      'IsInfected',
      'quarantined',
      'Quarantined',
      'isQuarantined',
      'IsQuarantined'
    ]);

    if (isTruthyRecordFlag(explicitVirusFlag)) {
      return true;
    }

    const scanValues = getAllRecordValues(record, [
      'scanStatus',
      'ScanStatus',
      'scanResult',
      'ScanResult',
      'scanResultStatus',
      'ScanResultStatus',
      'virusScanStatus',
      'VirusScanStatus',
      'virusScanResult',
      'VirusScanResult',
      'antivirusScanStatus',
      'AntivirusScanStatus',
      'antiVirusScanStatus',
      'AntiVirusScanStatus',
      'malwareScanStatus',
      'MalwareScanStatus',
      'malwareScanResult',
      'MalwareScanResult',
      'threatScanStatus',
      'ThreatScanStatus',
      'threatScanResult',
      'ThreatScanResult',
      'avScanStatus',
      'AvScanStatus',
      'fileScanStatus',
      'FileScanStatus',
      'mediaScanStatus',
      'MediaScanStatus',
      'recordStatus',
      'RecordStatus',
      'status',
      'Status',
      'fileStatus',
      'FileStatus',
      'uploadStatus',
      'UploadStatus',
      'workflowStatus',
      'WorkflowStatus',
      'processingStatus',
      'ProcessingStatus',
      'verdict',
      'Verdict',
      'scanVerdict',
      'ScanVerdict'
    ]);

    return scanValues.some((value) => isVirusStatus(value));
  }

  function isTranscriptVirusDetected(mediaRecord, transcriptRecord) {
    const effectiveTranscriptRecord = getEffectiveTranscriptRecord(mediaRecord, transcriptRecord);
    return Boolean(effectiveTranscriptRecord) && isVirusDetectedRecord(effectiveTranscriptRecord);
  }

  function isScanningLifecycleStatus(value) {
    const normalized = normalizeValue(value);
    const mediaRecordStatus = resolveMediaRecordStatusValue(value);
    return normalized === 'pending'
      || normalized === 'queued'
      || normalized === 'uploading'
      || normalized === 'uploaded'
      || normalized === 'processing'
      || normalized === 'inprogress'
      || normalized === 'in-progress'
      || mediaRecordStatus === 'draft'
      || mediaRecordStatus === 'unsubmitted'
      || normalized.includes('scan')
      || normalized.includes('upload')
      || normalized.includes('process');
  }

  function hasExplicitCleanScanStatus(record) {
    const scanStatus = getRecordScanStatus(record);
    return Boolean(scanStatus) && isSuccessfulStatus(scanStatus);
  }

  function hasExplicitVirusScanStatus(record) {
    const scanStatus = getRecordScanStatus(record);
    return Boolean(scanStatus) && isVirusStatus(scanStatus);
  }

  function resolveToneFromLocalStatus(scanStatus) {
    const normalized = resolveScanStatusValue(scanStatus);

    if (isVirusStatus(normalized)) {
      return 'quarantine';
    }

    if (isFailedStatus(normalized)) {
      return 'failed';
    }

    if (isSuccessfulStatus(normalized)) {
      return 'clean';
    }

    if (normalized === 'scanning' || normalized === 'uploaded' || normalized.includes('scan') || normalized.includes('upload')) {
      return 'scan';
    }

    return 'ready';
  }

  function resolveHigherPriorityTone(primaryTone, fallbackTone) {
    const priority = {
      quarantine: 50,
      failed: 45,
      clean: 40,
      busy: 30,
      scan: 20,
      ready: 10
    };

    return (priority[fallbackTone] || 0) > (priority[primaryTone] || 0)
      ? fallbackTone
      : primaryTone;
  }

  function resolveRemoteMediaTone(mediaRecord) {
    const scanStatus = resolveScanStatusValue(getRecordScanStatus(mediaRecord));

    if (scanStatus === 'failed' || isFailedStatus(scanStatus)) {
      return 'failed';
    }

    if (isVirusDetectedMediaRecord(mediaRecord) || isVirusStatus(scanStatus)) {
      return 'quarantine';
    }

    if (isSuccessfulStatus(scanStatus)) {
      return 'clean';
    }

    if (scanStatus.includes('scan') || scanStatus.includes('upload')) {
      return 'scan';
    }

    return 'ready';
  }

  function resolveRemoteTranscriptTone(transcriptRecord, mediaRecord = null) {
    const effectiveTranscriptRecord = getEffectiveTranscriptRecord(mediaRecord, transcriptRecord);
    if (!effectiveTranscriptRecord) {
      return 'ready';
    }

    const scanStatus = resolveScanStatusValue(getRecordScanStatus(effectiveTranscriptRecord));

    if (scanStatus === 'failed' || isFailedStatus(scanStatus)) {
      return 'failed';
    }

    if (hasExplicitVirusScanStatus(transcriptRecord)
      || isVirusDetectedRecord(effectiveTranscriptRecord)
      || isVirusStatus(scanStatus)) {
      return 'quarantine';
    }

    if (isSuccessfulStatus(scanStatus)) {
      return 'clean';
    }

    if (scanStatus.includes('scan') || scanStatus.includes('upload') || scanStatus === 'uploaded') {
      return 'scan';
    }

    return 'ready';
  }

  function resolveCaseFileMediaTone(mediaRecord, localUploadItem) {
    const remoteTone = resolveRemoteMediaTone(mediaRecord);
    if (!localUploadItem || !isUploadWorkflowComplete(localUploadItem)) {
      return remoteTone;
    }

    const localTone = resolveToneFromLocalStatus(localUploadItem.scanStatus);
    if (remoteTone === 'scan' || remoteTone === 'ready') {
      return resolveHigherPriorityTone(remoteTone, localTone);
    }

    return remoteTone;
  }

  function resolveCaseFileMediaTitle(mediaRecord, localUploadItem) {
    if (isVirusDetectedMediaRecord(mediaRecord)) {
      return 'Media scan: Quarantined';
    }

    const remoteStatus = resolveScanStatusValue(getRecordScanStatus(mediaRecord));
    if (remoteStatus && remoteStatus !== 'pending' && remoteStatus !== 'ready') {
      return resolveScanStatusTitle('Media scan', getRecordDisplayStatus(mediaRecord));
    }

    if (localUploadItem && isUploadWorkflowComplete(localUploadItem)) {
      const localStatus = resolveScanStatusValue(localUploadItem.scanStatus);
      if (isSuccessfulStatus(localStatus)) {
        return 'Media scan: Clean';
      }

      if (isVirusStatus(localStatus)) {
        return 'Media scan: Quarantined';
      }

      if (isFailedStatus(localStatus)) {
        return 'Media scan: Failed';
      }

      return 'Media scan: Scanning';
    }

    return resolveScanStatusTitle('Media scan', getRecordDisplayStatus(mediaRecord));
  }

  function resolveCaseFileTranscriptTone(transcriptRecord, localUploadItem, mediaRecord = null) {
    const remoteTone = resolveRemoteTranscriptTone(transcriptRecord, mediaRecord);
    if (!localUploadItem || !localUploadItem.transcriptEnabled || localUploadItem.transcriptUploadStatus !== 'completed') {
      return remoteTone;
    }

    const localTone = resolveToneFromLocalStatus(localUploadItem.transcriptScanStatus);
    if (remoteTone === 'scan' || remoteTone === 'ready') {
      return resolveHigherPriorityTone(remoteTone, localTone);
    }

    return remoteTone;
  }

  function resolveCaseFileTranscriptTitle(transcriptRecord, localUploadItem, mediaRecord = null) {
    const effectiveTranscriptRecord = getEffectiveTranscriptRecord(mediaRecord, transcriptRecord);
    if (isVirusDetectedRecord(effectiveTranscriptRecord)) {
      return 'Transcript scan: Quarantined';
    }

    const remoteStatus = resolveScanStatusValue(getRecordScanStatus(effectiveTranscriptRecord));
    if (remoteStatus && remoteStatus !== 'pending' && remoteStatus !== 'ready') {
      return resolveScanStatusTitle('Transcript scan', getRecordDisplayStatus(effectiveTranscriptRecord));
    }

    if (localUploadItem && localUploadItem.transcriptEnabled && localUploadItem.transcriptUploadStatus === 'completed') {
      const localStatus = resolveScanStatusValue(localUploadItem.transcriptScanStatus);
      if (isSuccessfulStatus(localStatus)) {
        return 'Transcript scan: Clean';
      }

      if (isVirusStatus(localStatus)) {
        return 'Transcript scan: Quarantined';
      }

      if (isFailedStatus(localStatus)) {
        return 'Transcript scan: Failed';
      }

      return 'Transcript scan: Scanning';
    }

    return resolveScanStatusTitle('Transcript scan', getRecordDisplayStatus(effectiveTranscriptRecord));
  }

  function isMediaCaseRecord(record) {
    const fileType = normalizeDisplayName(getRecordFileType(record));
    const extension = inferRecordExtension(record);
    const mimeType = getRecordMimeType(record);

    return fileType === 'media'
      || MEDIA_EXTENSIONS.has(fileType)
      || MEDIA_EXTENSIONS.has(extension)
      || mimeType.startsWith('video/')
      || mimeType.startsWith('audio/');
  }

  function isTranscriptCaseRecord(record) {
    const fileType = normalizeDisplayName(getRecordFileType(record));
    const extension = inferRecordExtension(record);
    const mimeType = getRecordMimeType(record);

    return fileType === 'transcript'
      || fileType === 'pdf'
      || TRANSCRIPT_EXTENSIONS.has(fileType)
      || TRANSCRIPT_EXTENSIONS.has(extension)
      || mimeType === 'application/pdf';
  }

  function isCleanCaseFileRecord(record) {
    if (!record) {
      return false;
    }

    const scanStatus = resolveScanStatusValue(getFirstRecordValue(record, ['scanStatus', 'ScanStatus', 'status', 'Status']));
    const lifecycleStatus = normalizeValue(getFirstRecordValue(record, ['lifecycleStatus', 'LifecycleStatus', 'fileStatus', 'FileStatus']));
    return scanStatus === 'clean' || scanStatus === 'completed' || scanStatus === 'complete' || scanStatus === 'success' || scanStatus === 'succeeded' || scanStatus === 'submitted' || scanStatus === 'safe' || scanStatus === 'benign' || scanStatus === 'pass' || scanStatus === 'passed' || scanStatus.includes('clean') || scanStatus.includes('no virus') || scanStatus.includes('novirus') || scanStatus.includes('no-virus') || scanStatus.includes('no_virus') || scanStatus.includes('no threat') || scanStatus.includes('nothreat') || scanStatus.includes('no-threat') || scanStatus.includes('no_threat') || scanStatus.includes('threatnotfound') || scanStatus.includes('noinfection') || scanStatus.includes('notinfected') || lifecycleStatus === 'available' || lifecycleStatus === 'approved' || lifecycleStatus === 'accepted';
  }

  function isCleanCaseFileItem(item) {
    if (!item) {
      return false;
    }

    const mediaClean = isCleanCaseFileRecord(item.mediaRecord);
    const transcriptClean = !item.transcriptRecord || isCleanCaseFileRecord(item.transcriptRecord);
    return mediaClean && transcriptClean;
  }

  function hasVirusDetectedInCaseFiles(records = [], buildCaseFileItems) {
    const rawRecords = Array.isArray(records) ? records : [];

    // Use the same grouped case-file view that the UI renders. Raw API payloads
    // can briefly contain orphan transcript rows after the parent media file is
    // deleted; those rows must not keep the virus banner alive after the visible
    // infected file has gone.
    return buildCaseFileItems(rawRecords).some((item) => {
      const mediaRecord = item?.mediaRecord ?? null;
      const transcriptRecord = getEffectiveTranscriptRecord(mediaRecord, item?.transcriptRecord ?? null);
      return isVirusDetectedRecord(mediaRecord) || isVirusDetectedRecord(transcriptRecord) || isTranscriptVirusDetected(mediaRecord, transcriptRecord);
    });
  }

  return {
    getAllRecordValues,
    isTruthyRecordFlag,
    isVirusDetectedRecord,
    isVirusDetectedMediaRecord,
    isTranscriptVirusDetected,
    getRecordScanStatus,
    getRecordLifecycleStatus,
    getRecordDisplayStatus,
    isScanningLifecycleStatus,
    hasExplicitCleanScanStatus,
    hasExplicitVirusScanStatus,
    resolveToneFromLocalStatus,
    resolveHigherPriorityTone,
    resolveRemoteMediaTone,
    resolveRemoteTranscriptTone,
    resolveCaseFileMediaTone,
    resolveCaseFileMediaTitle,
    resolveCaseFileTranscriptTone,
    resolveCaseFileTranscriptTitle,
    isMediaCaseRecord,
    isTranscriptCaseRecord,
    isCleanCaseFileRecord,
    isCleanCaseFileItem,
    hasVirusDetectedInCaseFiles,
    getRecordMediaId,
    getRecordTranscriptFileId,
    getRecordLogicalName,
    getRecordOriginalFileName,
    getRecordSizeInBytes
  };
}
